import React from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { graphql, useStaticQuery } from "gatsby";
import { imageBuilder, toPlainText } from "../../lib/helpers";
import { shouldShowNode, pageDocPath, authorPath } from "../../../helpers";
import clientConfig from "../../../client-config";

const SHARE_IMAGE_WIDTH = 1536;
const SHARE_IMAGE_QUALITY = 80;
const SITE_TITLE = "Common Room";

const shareImageUrl = (image) => {
  if (!image) return null;
  const builder = imageBuilder(image);
  if (!builder) return null;
  const nativeWidth = image?.asset?.metadata?.dimensions?.width;
  const needsResize = !nativeWidth || nativeWidth > SHARE_IMAGE_WIDTH;
  return (needsResize ? builder.width(SHARE_IMAGE_WIDTH) : builder)
    .quality(SHARE_IMAGE_QUALITY)
    .url();
};

const generateOrganizationSchema = (site) => {
  if (!site) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": SITE_TITLE,
    "url": clientConfig.site.host,
    "description": site.description || "",
  };

  const logoUrl = shareImageUrl(site.mainImage);
  if (logoUrl) {
    schema.logo = { "@type": "ImageObject", "url": logoUrl, "width": SHARE_IMAGE_WIDTH };
  }

  if (site.email) {
    schema.email = site.email;
    schema.contactPoint = { "@type": "ContactPoint", "contactType": "sales", "email": site.email };
  }

  const sameAs = [
    site.twitter ? `https://twitter.com/${site.twitter.replace('@', '')}` : null,
    site.linkedin?.startsWith('http') ? site.linkedin : site.linkedin ? `https://www.linkedin.com/company/${site.linkedin}` : null
  ].filter(Boolean);

  if (sameAs.length > 0) schema.sameAs = sameAs;

  return schema;
};

const generateContentSchema = (doc, image) => {
  if (!doc) return null;

  let schemaType = null;
  if (doc._type === "post") {
    schemaType = "BlogPosting";
  } else if (["documentation", "playbook"].includes(doc._type)) {
    schemaType = "HowTo";
  } else if (doc._type === "story") {
    schemaType = "Article";
  } else {
    return null;
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": schemaType,
    [schemaType === "HowTo" ? "name" : "headline"]: doc.title,
    "url": `${clientConfig.site.host}${pageDocPath(doc)}`,
  };

  if (doc._rawExcerpt) schema.description = toPlainText(doc._rawExcerpt);

  const imageToUse = image?.asset ? image : doc.mainImage?.asset ? doc.mainImage : null;
  if (imageToUse) {
    const contentImage = shareImageUrl(imageToUse);
    if (contentImage) schema.image = contentImage;
  }

  if (schemaType === "BlogPosting" || schemaType === "Article") {
    schema.datePublished = doc.publishedAt || doc._createdAt;
    schema.dateModified = doc._updatedAt;
    schema.publisher = { "@type": "Organization", "name": SITE_TITLE };

    if (doc.authors?.length > 0) {
      schema.author = doc.authors.map(a => {
        const person = { "@type": "Person", "name": a.author?.name };
        if (a.author?.name) person.url = `${clientConfig.site.host}${authorPath(a.author.name)}`;
        if (a.author?.title) person.jobTitle = a.author.title;
        if (a.author?.image?.asset) {
          const authorImage = imageBuilder(a.author.image)?.width(200)?.url();
          if (authorImage) person.image = authorImage;
        }
        return person;
      }).filter(p => p.name);
    } else {
      schema.author = { "@type": "Organization", "name": SITE_TITLE };
    }

    if (doc.categories?.length > 0) schema.articleSection = doc.categories[0].title;
  }

  if (schemaType === "Article" && doc.company?.title) {
    schema.about = { "@type": "Organization", "name": doc.company.title };
  }

  if (schemaType === "HowTo" && doc._rawBody?.length > 0) {
    const steps = doc._rawBody
      .filter(block => block.style === "h2" && block.children?.[0]?.text)
      .map(block => ({
        "@type": "HowToStep",
        "name": block.children[0].text,
      }));

    if (steps.length > 0) schema.step = steps;
  }

  return schema;
};

const generateProfilePageSchema = (doc) => {
  if (doc?._type !== "author" || !doc.name) return null;

  const person = {
    "@type": "Person",
    "name": doc.name,
    "url": `${clientConfig.site.host}${authorPath(doc.name)}`,
  };

  if (doc.title) person.jobTitle = doc.title;
  if (doc._rawBio) person.description = toPlainText(doc._rawBio);

  if (doc.image?.asset) {
    const img = imageBuilder(doc.image)?.width(400)?.url();
    if (img) person.image = img;
  }

  const sameAs = [
    doc.twitter ? `https://twitter.com/${doc.twitter}` : null,
    doc.linkedin || null,
  ].filter(Boolean);
  if (sameAs.length) person.sameAs = sameAs;

  if (doc.companies?.[0]?.title) {
    person.worksFor = { "@type": "Organization", "name": doc.companies[0].title };
  }

  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "mainEntity": person,
  };
};

const PageHeadMeta = ({
  description,
  lang,
  meta,
  keywords,
  title,
  image,
  bodyAttributes,
  isTitleOverriden,
  path,
  doc,
  skipImage,
}) => {
  const { site } =
    useStaticQuery(graphql`
      query DefaultSEOQuery {
        site: sanitySiteSettings(_id: { eq: "siteSettings" }) {
          description
          twitter
          linkedin
          email
          mainImage {
            ...SanityImageSimple
          }
        }
      }
    `) || {};

  const trueTitle = isTitleOverriden
    ? title || doc.title
    : `${title || doc.title} | ${SITE_TITLE}`;
  const metaDescription =
    description ||
    toPlainText(doc?._rawExcerpt) ||
    (doc?._type === "author"
      ? (doc._rawBio ? toPlainText(doc._rawBio) : `All blog posts written by ${doc.name}.`)
      : null) ||
    site?.description || "";

  const pageUrl = process.env.HOST + (path || (doc ? pageDocPath(doc) : ""));
  const isHidden =
    (doc?.status && doc?.status !== "published") ||
    doc?.contentType === "landing" ||
    doc?._type === "company" ||
    doc?._type === "competeDB" ||
    (["playbook", "post"].includes(doc?._type) && !shouldShowNode(doc));

  const metaImage = shareImageUrl(
    image?.asset ? image : doc?.mainImage?.asset ? doc.mainImage : site?.mainImage
  );

  const twitter = `@${site?.twitter}`;
  const twitterTags = {
    card: "summary_large_image",
    site: twitter,
    creator: twitter,
    title,
    description: metaDescription,
    image: metaImage,
    "image:width": SHARE_IMAGE_WIDTH,
    "image:alt":
      doc?.mainImage?.asset
        ? title
        : "Common Room share preview",
  };
  const twitterTagsList = [];
  Object.entries(twitterTags).forEach(([k, v]) =>
    twitterTagsList.push({ property: "twitter:" + k, content: v })
  );

  const includeSanityClient =
    ["help", "resources", "resources-new"].includes(doc?.slug?.current) ||
    ["documentation", "category"].includes(doc?._type);

  const organizationSchema = generateOrganizationSchema(site);
  const contentSchema = generateContentSchema(doc, image);
  const profilePageSchema = generateProfilePageSchema(doc);

  return (
    <Helmet
      bodyAttributes={bodyAttributes}
      htmlAttributes={{ lang }}
      title={trueTitle}
      meta={[
        {
          name: "description",
          content: metaDescription,
        },
        {
          property: "og:title",
          content: trueTitle,
        },
        {
          property: "og:description",
          content: metaDescription,
        },
        {
          property: "og:type",
          content: "website",
        },
        {
          property: "og:site_name",
          content: SITE_TITLE,
        },
        {
          property: "og:url",
          content: pageUrl,
        },
        {
          property: "og:see_also",
          content: `https://twitter.com/${twitter}`,
        },
        {
          property: "og:image",
          content: skipImage ? undefined : metaImage,
        },
      ]
        .concat(doc?._createdAt || doc?._updatedAt ?
          [{
            property: "dateCreated",
            content: doc?._createdAt,
          },
          {
            property: "dateModified",
            content: doc?._updatedAt,
          }] : []
        )
        .concat(isHidden ? { name: "robots", content: "noindex,nofollow" } : [])
        .concat(
          keywords && keywords.length > 0
            ? {
              name: "keywords",
              content: keywords.join(", "),
            }
            : []
        )
        .concat(meta)
        .concat(twitterTagsList)}
      script={[
        // Sanity client UMD build for browser-side search — version pinned because
        // the unpkg default now serves an ESM-only build with no UMD/global export
        ...(includeSanityClient ? [{ src: "https://unpkg.com/@sanity/client@6.2.0" }] : []),
        // Organization JSON-LD schema
        ...(organizationSchema ? [{
          type: "application/ld+json",
          innerHTML: JSON.stringify(organizationSchema),
        }] : []),
        // Content JSON-LD schema (BlogPosting or HowTo)
        ...(contentSchema ? [{
          type: "application/ld+json",
          innerHTML: JSON.stringify(contentSchema),
        }] : []),
        // Author profile page schema
        ...(profilePageSchema ? [{
          type: "application/ld+json",
          innerHTML: JSON.stringify(profilePageSchema),
        }] : []),
      ]}
      link={[
        {
          rel: "icon",
          type: "image/svg+xml",
          href: "/static/meta/favicon.svg",
        },
        {
          rel: "icon",
          type: "image/png",
          sizes: "64x64",
          href: "/static/meta/favicon.png",
        },
        // {
        //   rel: "icon",
        //   type: "image/png",
        //   sizes: "16x16",
        //   href: "/static/meta/favicon-16x16.png",
        // },
        {
          rel: "apple-touch-icon",
          sizes: "180x180",
          href: "/static/meta/apple-touch-icon.png",
        },
        {
          rel: "dns-prefetch",
          href: "https://www.googletagmanager.com/",
        },
      ]
        .concat(
          doc && doc._type && !isHidden
            ? {
              rel: "canonical",
              href: pageUrl,
            }
            : []
        )
        .concat(
          ["/", "get-started"].includes(doc?.slug?.current)
            ? {
              rel: "alternate",
              type: "text/markdown",
              title: "LLM-friendly",
              href: "/llms.txt",
            }
            : []
        )}
    />
  );
};

PageHeadMeta.defaultProps = {
  lang: "en",
  meta: [],
  keywords: [],
};

PageHeadMeta.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  title: PropTypes.string,
};

export default PageHeadMeta;
